import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

import { HistoryCard, UserFill } from './HistoryCard';
import { HexString } from '@/lib/config/wagmi';
import { useTranslations } from 'next-intl';
import { Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useUserFills } from '@/lib/hooks/useUserFills';
import type { UserFills } from '@/lib/utils/hyper/types'
import TouchableOpacity from '../ui/TouchableOpacity';

interface HistorySectionProps {
  userAddress: HexString;
  onViewAll?: () => void;
  hideHeader?: boolean;
}

export function HistoryContainer({
  userFills,
  scrollMargin,
}: {
  userFills: UserFills
  scrollMargin: number
}) {
  const fillVirtualizer = useWindowVirtualizer({
    count: userFills.length,
    estimateSize: () => 50 + 6 * 4,
    scrollMargin: scrollMargin
  });

  const keyExtractor = useCallback((item: UserFill) => item.tid.toString(), []);

  // Helper function to format time relative to now
  const getTimeLabel = useCallback((timestamp: number) => {
    const now = Date.now() / 1000;
    const diff = now - timestamp;

    if (diff < 60) {
      return { type: 'relative', label: `${Math.floor(diff)}S Ago` };
    } else if (diff < 3600) {
      return { type: 'relative', label: `${Math.floor(diff / 60)}M Ago` };
    } else if (diff < 86400) {
      return { type: 'relative', label: `${Math.floor(diff / 3600)}H Ago` };
    }
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear().toString().slice(2);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return {
      type: 'absolute',
      date: `${year}.${month}.${day}`,
      time: `${hours}:${minutes}`,
    };
  }, []);

  const renderHistoryItem: React.FC<{ item: UserFill; index: number }> =
    useCallback(
      ({ item: fill, index }) => {
        const timeData = getTimeLabel(fill.time / 1000);

        return (
          <HStack w="full" align="start" gap={4}>
            {/* Timeline on the left */}
            <VStack w="50px" gap={0} align="center">
              {/* Time badge */}
              <VStack
                w="full"
                h="50px"
                align="center"
                justify="center"
                rounded="full"
                bg="text.t1/10"
                px={2}
              >
                {timeData.type === 'relative' ? (
                  <Text
                    textAlign="center"
                    fontSize="0.6875rem"
                    color="text.t1"
                    fontWeight="medium"
                  >
                    {timeData.label}
                  </Text>
                ) : (
                  <>
                    <Text
                      textAlign="center"
                      fontSize="0.625rem"
                      color="text.t1"
                      fontWeight="medium"
                    >
                      {timeData.date}
                    </Text>
                    <Text
                      textAlign="center"
                      fontSize="0.625rem"
                      color="text.t1/60"
                      fontWeight="medium"
                    >
                      {timeData.time}
                    </Text>
                  </>
                )}
              </VStack>

              {/* Connector line (except for last item) */}
              {index < userFills.length - 1 && (
                <HStack w={3} justify="center">
                  <Box
                    h={6}
                    borderLeft="1px dashed rgba(255, 255, 255, 0.3)"
                  ></Box>
                </HStack>
              )}
            </VStack>

            {/* History card on the right */}
            <HistoryCard fill={fill} />
          </HStack>
        );
      },
      [getTimeLabel, userFills.length],
    );

  return (
    <Box
      pos="relative"
      height={`${fillVirtualizer.getTotalSize()}px`}
    >
      {fillVirtualizer.getVirtualItems().map((virtualItem) => (
        <Box
          key={keyExtractor(userFills[virtualItem.index])}
          pos="absolute"
          top={0}
          left={0}
          h={`${virtualItem.size}px`}
          w="full"
          transform={`translateY(${virtualItem.start - scrollMargin}px)`}
        >
          {renderHistoryItem({ item: userFills[virtualItem.index], index: virtualItem.index })}
        </Box>
      ))}
    </Box>
  )
}

export function HistorySection({
  userAddress,
}: HistorySectionProps) {
  const t = useTranslations();

  const { data: userFills, isLoading, error } = useUserFills(userAddress);

  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollMargin, setScrollMargin] = useState(0)

  useEffect(() => {
    if (containerRef.current) {
      const updateScrollMargin = () => {
        setScrollMargin(containerRef.current?.offsetTop || 0)
      }

      // Initial update
      updateScrollMargin()

      // Update on resize
      window.addEventListener('resize', updateScrollMargin)

      return () => {
        window.removeEventListener('resize', updateScrollMargin)
      }
    }
  }, [userFills])

  return (
    <Box
      ref={containerRef}
      w="full"
      px={4}
    >
      {isLoading ? (
        <Box h={10} textAlign="center">
          <Spinner color="text.t1" />
        </Box>
      ) : error ? (
        <Box rounded="xl" p={4} bg="bg.t0/5">
          <Text textAlign="center" fontSize="sm" color="red.400">
            {t('trader.history.error')}
          </Text>
        </Box>
      ) : !userFills || userFills.length === 0 ? (
        <Box rounded="xl" p={4} bg="bg.t0/5">
          <Text textAlign="center" fontSize="sm" color="text.t1/60">
            {t('trader.history.noHistory')}
          </Text>
        </Box>
      ) : (
        <HistoryContainer userFills={userFills} scrollMargin={scrollMargin} />
      )}
    </Box>
  );
}
